import type { planEnrollmentApiSpec } from "@wingspanhq/wingspan-api-types";
import { useMemo } from "react";
import { useQueryActiveMeridioPlanEnrollments } from "../../../query/benefits/useQueryActiveMeridioPlanEnrollments";
import { useQueryPlanEnrollments } from "../../../query/benefits/useQueryPlanEnrollments";
import { useGetNow } from "../../../shared/hooks/useGetNow";
import { Benefit } from "../data";
import { selectorAvailablePlanEnrollments } from "../selectors/selectorAvailablePlanEnrollments";

type IPlanEnrollment = planEnrollmentApiSpec.PlanEnrollment;
type IPlanType = IPlanEnrollment["planType"];

/**
 * Resolves every benefit's enrollment in two requests for the whole page.
 *
 * The CMS-era cards each issued their own filtered `/v2/plan-enrollment` call,
 * so a list of N insurance cards cost N requests.
 */
export const useBenefitsEnrollments = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  const now = useGetNow();

  const queryPlanEnrollments = useQueryPlanEnrollments(
    { sort: "createdAt" },
    { enabled }
  );
  const queryMeridioEnrollments = useQueryActiveMeridioPlanEnrollments({
    enabled
  });

  const enrollmentsByPlanType = useMemo(() => {
    const available = [
      ...selectorAvailablePlanEnrollments(queryPlanEnrollments.data ?? [], now),
      ...selectorAvailablePlanEnrollments(
        queryMeridioEnrollments.data ?? [],
        now
      )
    ];

    return available.reduce<Partial<Record<IPlanType, IPlanEnrollment>>>(
      (acc, enrollment) =>
        acc[enrollment.planType]
          ? acc
          : { ...acc, [enrollment.planType]: enrollment },
      {}
    );
  }, [queryPlanEnrollments.data, queryMeridioEnrollments.data, now]);

  return {
    isLoading:
      queryPlanEnrollments.isLoading || queryMeridioEnrollments.isLoading,
    enrollmentFor: (benefit: Benefit) =>
      benefit.enrolledPlanType
        ? enrollmentsByPlanType[benefit.enrolledPlanType]
        : undefined,
    isEnrolled: (benefit: Benefit) =>
      !!benefit.enrolledPlanType &&
      !!enrollmentsByPlanType[benefit.enrolledPlanType]
  };
};
